import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wrench,
  ChevronDown,
  Gauge,
  Wifi,
  WifiOff,
  Bell,
  Infinity,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface DevTestingPanelProps {
  collapsed?: boolean;
  onExpandSidebar?: () => void;
}

// Short duration for dev tool confirmations
const DEV_TOAST_DURATION = 3000;

export function DevTestingPanel({ collapsed, onExpandSidebar }: DevTestingPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const { toast } = useToast();

  // Listen for online restored event from OfflineBanner
  useEffect(() => {
    const handleOnlineRestored = () => {
      setIsOffline(false);
    };

    window.addEventListener('dev-online-restored', handleOnlineRestored);
    return () => window.removeEventListener('dev-online-restored', handleOnlineRestored);
  }, []);

  // Credit test buttons
  const setCredits = (amount: number) => {
    if (typeof (window as any).__setTestCredits === 'function') {
      (window as any).__setTestCredits(amount);
      toast({
        title: 'Dev: Credits Set',
        description: `Credits set to ${amount}/20`,
        duration: DEV_TOAST_DURATION,
      });
    }
  };

  const setUnlimited = () => {
    if (typeof (window as any).__setUnlimited === 'function') {
      (window as any).__setUnlimited();
      toast({
        title: 'Dev: Unlimited Mode',
        description: 'Account set to unlimited/exempt',
        duration: DEV_TOAST_DURATION,
      });
    }
  };

  const resetCredits = () => {
    if (typeof (window as any).__resetCredits === 'function') {
      (window as any).__resetCredits();
      toast({
        title: 'Dev: Credits Reset',
        description: 'Credits reset to defaults (20/20)',
        duration: DEV_TOAST_DURATION,
      });
    }
  };

  // Offline simulation
  const toggleOffline = () => {
    const newOffline = !isOffline;
    setIsOffline(newOffline);
    
    // Dispatch custom event that OfflineBanner can listen to
    window.dispatchEvent(new CustomEvent('dev-offline-toggle', { 
      detail: { offline: newOffline } 
    }));
    
    toast({
      title: newOffline ? 'Dev: Offline Mode' : 'Dev: Online Mode',
      description: newOffline 
        ? 'Simulating offline connection' 
        : 'Connection restored',
      variant: newOffline ? 'destructive' : 'default',
      duration: DEV_TOAST_DURATION,
    });
  };

  // Toast testing
  const triggerToast = (type: 'success' | 'error' | 'warning' | 'info') => {
    const toasts = {
      success: { title: 'Success!', description: 'This is a success toast', variant: 'success' as const, duration: DEV_TOAST_DURATION },
      error: { title: 'Error!', description: 'This is an error toast', variant: 'destructive' as const, duration: DEV_TOAST_DURATION },
      warning: { title: 'Warning!', description: 'This is a warning toast', variant: 'warning' as const, duration: DEV_TOAST_DURATION },
      info: { title: 'Info', description: 'This is an info toast', variant: 'default' as const, duration: DEV_TOAST_DURATION },
    };
    toast(toasts[type]);
  };

  // Handle collapsed click - expand sidebar and open panel
  const handleCollapsedClick = () => {
    if (onExpandSidebar) {
      onExpandSidebar();
    }
    setIsOpen(true);
  };

  // Collapsed view - just show icon
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleCollapsedClick}
            className={cn(
              'w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors',
              'text-amber-500 hover:bg-amber-500/10',
              isOpen && 'bg-amber-500/10'
            )}
          >
            <Wrench className="w-5 h-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          Dev Tools
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors overflow-hidden',
            'text-amber-500 hover:bg-amber-500/10',
            isOpen && 'bg-amber-500/10'
          )}
        >
          <Wrench className="w-5 h-5 flex-shrink-0" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className="text-sm font-medium flex-1 text-left whitespace-nowrap overflow-hidden"
              >
                Dev Tools
              </motion.span>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto', rotate: isOpen ? 180 : 0 }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className="flex-shrink-0 overflow-hidden"
              >
                <ChevronDown className="w-4 h-4" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </CollapsibleTrigger>

      <AnimatePresence>
        {isOpen && (
          <CollapsibleContent forceMount asChild>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-2 py-2 space-y-3 min-w-[200px]">
                {/* Rate Limit Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-2 text-xs font-medium text-muted-foreground">
                    <Gauge className="w-3 h-3" />
                    Credits
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {[0, 2, 5, 10].map((amount) => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setCredits(amount)}
                      >
                        {amount}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={setUnlimited}
                    >
                      <Infinity className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={resetCredits}
                    >
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Connection Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-2 text-xs font-medium text-muted-foreground">
                    {isOffline ? <WifiOff className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
                    Connection
                  </div>
                  <Button
                    variant={isOffline ? 'destructive' : 'outline'}
                    size="sm"
                    className="w-full h-7 text-xs"
                    onClick={toggleOffline}
                  >
                    {isOffline ? 'Go Online' : 'Simulate Offline'}
                  </Button>
                </div>

                {/* Toast Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-2 text-xs font-medium text-muted-foreground">
                    <Bell className="w-3 h-3" />
                    Toasts
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-green-600"
                      onClick={() => triggerToast('success')}
                    >
                      Success
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-red-600"
                      onClick={() => triggerToast('error')}
                    >
                      Error
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-amber-600"
                      onClick={() => triggerToast('warning')}
                    >
                      Warn
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </CollapsibleContent>
        )}
      </AnimatePresence>
    </Collapsible>
  );
}
